import { Currency } from "@redotech/money/currencies";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

export enum PriceConstraintKind {
  DEFAULT = "DEFAULT",
  RELATIVE_TO_OTHER_RATE_TABLE = "RELATIVE_TO_OTHER_RATE_TABLE",
}

const PriceConstraintInequalitySchema = z.object({
  amount: z.number().nullish(),
  percentage: z.number().nullish(),
});
export type PriceConstraintInequality = z.infer<
  typeof PriceConstraintInequalitySchema
>;

export const PriceConstraintSchema = z.union([
  /**
   * Apply margin constraints, then apply price constraints.
   */
  z.object({
    kind: z.literal(PriceConstraintKind.DEFAULT),
    currency: createEnum(Currency, { enumName: "Currency" }),
    /**
     * Absolute price bounds that the rate table price must be within.
     */
    priceConstraints: z
      .object({
        atLeast: z.object({ amount: z.number().nullish() }).nullish(),
        atMost: z.object({ amount: z.number().nullish() }).nullish(),
      })
      .nullish(),
    /**
     * Suggested margin bounds that the rate table price should be within
     * (relative to the rate table price).
     */
    marginConstraints: z
      .object({
        atLeast: PriceConstraintInequalitySchema.nullish(),
        atMost: PriceConstraintInequalitySchema.nullish(),
      })
      .nullish(),
  }),
  z.object({
    kind: z.literal(PriceConstraintKind.RELATIVE_TO_OTHER_RATE_TABLE),
    currency: createEnum(Currency, { enumName: "Currency" }),
    /**
     * Relative price bounds that the rate table price must be within.
     */
    priceConstraints: z.object({
      relativeToRateTableId: z.string(),
      byAtLeast: PriceConstraintInequalitySchema.nullish(),
      /**
       * Interpreted min price = MAX(absoluteMinimumPrice, reference rate table price + byAtLeast)
       */
      absoluteMinimumPrice: z.object({ amount: z.number() }).nullish(),
      byAtMost: PriceConstraintInequalitySchema.nullish(),
    }),
    /**
     * Suggested margin bounds that the rate table price should be within
     * (relative to the rate table price).
     */
    marginConstraints: z
      .object({
        atLeast: PriceConstraintInequalitySchema.nullish(),
        atMost: PriceConstraintInequalitySchema.nullish(),
      })
      .nullish(),
  }),
]);
export type PriceConstraint = z.infer<typeof PriceConstraintSchema>;
